
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardContent } from '@/components/dashboard/DashboardContent';
import { MainNavigation } from '@/components/layout/MainNavigation';
import { Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { resendVerificationEmail } from '@/services/auth.service';
import { toast } from 'sonner';
import { OnboardingModal } from '@/components/onboarding/OnboardingModal';
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus';

const Dashboard = () => {
  const { user, isAuthenticated, isLoading, isEmailVerified } = useAuth();
  const navigate = useNavigate();
  const [isEmailResending, setIsEmailResending] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const onboardingStatus = useOnboardingStatus();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      console.log('Dashboard: User not authenticated, redirecting to login');
      navigate('/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Check if onboarding should be shown
  useEffect(() => {
    if (isAuthenticated && isEmailVerified && !onboardingStatus.isLoading) {
      const shouldShowOnboarding = !onboardingStatus.isOnboardingComplete && 
                                 (onboardingStatus.needsPhone || onboardingStatus.needsOrganizationName || onboardingStatus.needsEmail);
      setShowOnboarding(shouldShowOnboarding);
    }
  }, [isAuthenticated, isEmailVerified, onboardingStatus]);

  const handleResendEmail = async () => {
    if (!user?.email || !user?.id) return;
    
    setIsEmailResending(true);
    try {
      await resendVerificationEmail(user.email, user.id);
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error: any) {
      toast.error(error.message || 'Failed to send verification email');
    } finally {
      setIsEmailResending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading...</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Show email verification screen if user hasn't verified their email
  if (!isEmailVerified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white to-green-50">
        <MainNavigation />
        <div className="flex items-center justify-center min-h-screen pt-16">
          <div className="max-w-md w-full space-y-8 p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-orange-100 mb-4">
                <Mail className="h-8 w-8 text-orange-600" />
              </div>
              <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
                Email Verification Required
              </h2>
              <p className="text-gray-600 mb-6">
                Please verify your email address to access your dashboard. We've sent a verification link to:
              </p>
              <p className="font-semibold text-gray-900 mb-6">{user?.email}</p>
              <p className="text-sm text-gray-500 mb-8">
                Click the link in the email to verify your account. If you don't see the email, check your spam folder.
              </p>
            </div>
            
            <div className="space-y-4">
              <Button
                onClick={handleResendEmail}
                disabled={isEmailResending}
                className="w-full py-6"
              >
                {isEmailResending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Sending verification email...
                  </>
                ) : (
                  'Resend verification email'
                )}
              </Button>
              
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                className="w-full py-6"
              >
                Back to Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      <main className="pt-16">
        <DashboardContent />
      </main>
      
      <OnboardingModal
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        needsPhone={onboardingStatus.needsPhone}
        needsOrganizationName={onboardingStatus.needsOrganizationName}
        needsEmail={onboardingStatus.needsEmail}
      />
    </div>
  );
};

export default Dashboard;
